<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { Alert, AlertV2, Button, ButtonV2, Container, Modal } from '@Shelly/shelly-design-system';

	import { CAMERA_CLOUD_STORAGE_SHOP_URL } from 'src/helpers/camera_cloud_storage_helpers';
	import { navigateToShop } from 'src/components/Lists/list-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	/**
	 * Shown when the user tries to subscribe to / upgrade / renew a
	 * Camera Cloud Storage plan but the account does not have any
	 * camera device in the profile. The "Purchase now" CTA forwards
	 * the user to the Shelly shop via `navigateToShop`, which handles
	 * the native-app webview vs. browser tab split.
	 *
	 * Reused by `CameraCloudStorageSection` (Subscribe / Upgrade plan
	 * cards) and `CameraCloudStoragePlanInfoCard` (the inline Upgrade
	 * now / Renew now actions surfaced on the Premium plan card).
	 */
	export let showModal = false;

	const dispatch = createEventDispatcher<{ close: void }>();

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	function handleClose() {
		showModal = false;
		dispatch('close');
	}

	function handlePurchase() {
		navigateToShop(CAMERA_CLOUD_STORAGE_SHOP_URL);
		handleClose();
	}
</script>

{#if showModal}
	<Modal
		{showModal}
		on:close={handleClose}
		title={$format('modal_title_no_camera_device', {
			default: 'This plan needs a Shelly Camera',
		})}
		removeScrollPadding
	>
		<Container slot="content" padding="0" class="pl-m pr-m">
			{#if $showNewLayout}
				<AlertV2
					heading={$format('alert_no_camera_device_in_profile', {
						default:
							"You don't have currently any camera device in the profile. If you want to subscribe to a camera plan, you must have a camera device in the profile.",
					})}
				/>
			{:else}
				<Alert
					type="info"
					heading={$format('alert_no_camera_device_in_profile', {
						default:
							"You don't have currently any camera device in the profile. If you want to subscribe to a camera plan, you must have a camera device in the profile.",
					})}
				/>
			{/if}
		</Container>

		<div slot="footer" class="d-flex justify-content-end">
			<svelte:component
				this={ButtonComp}
				size="m"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				on:click={handlePurchase}
			>
				{$format('button_purchase_now', { default: 'Purchase now' })}
			</svelte:component>
		</div>
	</Modal>
{/if}
